import React from "react";
import {
  ShieldWarning, Package, User, ChatCircle, GoogleLogo, PaintBrushBroad,
  SignIn, Truck, ClipboardText, Database, Question,
} from "@phosphor-icons/react";

/**
 * Kelompok kategori untuk action audit trail.
 * Urutan menentukan prioritas kalau ada action yang match beberapa prefix.
 * Setiap category punya label, warna (Tailwind classes), dan icon.
 */
const CATEGORIES = [
  {
    key: "fraud",
    label: "SPB Palsu",
    color: "bg-red-100 text-red-800 border-red-300",
    dotColor: "bg-red-500",
    Icon: ShieldWarning,
    match: (a) => a === "spb.verify_failed",
  },
  {
    key: "verify",
    label: "Verifikasi",
    color: "bg-green-100 text-green-800 border-green-300",
    dotColor: "bg-green-500",
    Icon: ClipboardText,
    match: (a) => a === "spb.verify_via_qr" || a === "spb.status_change",
  },
  {
    key: "spb",
    label: "SPB",
    color: "bg-blue-100 text-blue-800 border-blue-300",
    dotColor: "bg-blue-500",
    Icon: Truck,
    match: (a) => a.startsWith("spb."),
  },
  {
    key: "auth",
    label: "Auth",
    color: "bg-purple-100 text-purple-800 border-purple-300",
    dotColor: "bg-purple-500",
    Icon: SignIn,
    match: (a) => a.startsWith("auth.") || ["login", "logout"].includes(a),
  },
  {
    key: "master",
    label: "Master Data",
    color: "bg-amber-100 text-amber-800 border-amber-300",
    dotColor: "bg-amber-500",
    Icon: Database,
    match: (a) => (
      a.startsWith("district.") || a.startsWith("plant.") ||
      a.startsWith("product.") || a.startsWith("truck.") ||
      a.startsWith("user.") || a.startsWith("signature.") ||
      a.startsWith("master.")
    ),
  },
  {
    key: "so",
    label: "Sales Order",
    color: "bg-teal-100 text-teal-800 border-teal-300",
    dotColor: "bg-teal-500",
    Icon: Package,
    match: (a) => a.startsWith("so."),
  },
  {
    key: "wa",
    label: "WhatsApp",
    color: "bg-green-100 text-green-700 border-green-300",
    dotColor: "bg-green-600",
    Icon: ChatCircle,
    match: (a) => a.startsWith("whatsapp.") || a.startsWith("telegram."),
  },
  {
    key: "sheets",
    label: "Sheets",
    color: "bg-emerald-100 text-emerald-800 border-emerald-300",
    dotColor: "bg-emerald-500",
    Icon: GoogleLogo,
    match: (a) => a.startsWith("sheets."),
  },
  {
    key: "layout",
    label: "Layout",
    color: "bg-pink-100 text-pink-800 border-pink-300",
    dotColor: "bg-pink-500",
    Icon: PaintBrushBroad,
    match: (a) => a.startsWith("layout.") || a.startsWith("settings."),
  },
  {
    key: "proof",
    label: "Bukti Muat",
    color: "bg-orange-100 text-orange-800 border-orange-300",
    dotColor: "bg-orange-500",
    Icon: User,
    match: (a) => a.startsWith("proof."),
  },
];

const FALLBACK = {
  key: "other",
  label: "Lainnya",
  color: "bg-gray-100 text-gray-700 border-gray-300",
  dotColor: "bg-gray-500",
  Icon: Question,
};

/**
 * Klasifikasikan action string ke satu kategori.
 * Return { key, label, color, dotColor, Icon }.
 */
export function categorizeAction(action) {
  const a = (action || "").toLowerCase();
  return CATEGORIES.find((c) => c.match(a)) || FALLBACK;
}

/** Return list of all categories for filter UI. */
export function getAllCategories() {
  return CATEGORIES;
}

/**
 * Badge component — displays category label + color + icon inline.
 * Small pill next to the action name in audit table.
 */
export function AuditCategoryBadge({ action, className = "" }) {
  const cat = categorizeAction(action);
  const Icon = cat.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${cat.color} ${className}`}
      data-testid={`audit-category-${cat.key}`}
      title={`Kategori: ${cat.label}`}
    >
      <Icon size={11} weight="fill" />
      {cat.label}
    </span>
  );
}
